import React, { useState } from 'react';
import { useProfile } from '../contexts/ProfileContext';
import { generateVCard, downloadVCard, getVCardQRData } from '../utils/vcard';
import { QRCodeSVG } from 'qrcode.react';
import { FiDownload, FiUser, FiCopy, FiCheck } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import toast from 'react-hot-toast';

const VCardExport = () => {
  const { profile, blocks } = useProfile();
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!profile) {
    return null;
  }

  const handleDownload = () => {
    try {
      const vcardContent = generateVCard(
        { ...profile, blocks },
        {
          includeAvatar: true,
          includeSocial: true,
          includeWallet: true,
        }
      );
      
      const filename = profile.username || `hubid-${profile.display_name || 'contact'}`;
      downloadVCard(vcardContent, filename);
      toast.success('vCard downloaded successfully!');
    } catch (error) {
      console.error('Error generating vCard:', error);
      toast.error('Failed to generate vCard');
    }
  };

  const handleCopy = async () => {
    try {
      const vcardContent = generateVCard(
        { ...profile, blocks },
        {
          includeAvatar: true,
          includeSocial: true,
          includeWallet: true,
        }
      );
      
      await navigator.clipboard.writeText(vcardContent);
      setCopied(true);
      toast.success('vCard copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying vCard:', error);
      toast.error('Failed to copy vCard');
    }
  };

  const vcardContent = profile
    ? generateVCard(
        { ...profile, blocks },
        {
          includeAvatar: true,
          includeSocial: true,
          includeWallet: true,
        }
      )
    : '';

  return (
    <div className="glass-dark p-6 rounded-xl border border-dark-700">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-lime-bright flex items-center">
            <SafeIcon icon={FiUser} className="mr-2" />
            Export Contact Card
          </h3>
          <p className="text-sm text-dark-400 mt-1">
            Download or share your contact information as a vCard (.vcf)
          </p>
        </div>
      </div>

      {/* Preview */}
      <div className="mb-6 p-4 bg-dark-800 rounded-lg border border-dark-700">
        <div className="text-sm text-dark-300 space-y-1">
          <p><span className="text-dark-400">Name:</span> {profile.display_name || profile.username}</p>
          {profile.bio && (
            <p><span className="text-dark-400">Bio:</span> {profile.bio}</p>
          )}
          {profile.wallet_address && (
            <p><span className="text-dark-400">Wallet:</span> {profile.wallet_address.slice(0, 6)}...{profile.wallet_address.slice(-4)}</p>
          )}
          {profile.username && (
            <p><span className="text-dark-400">Profile:</span> {window.location.origin}/#/u/{profile.username}</p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleDownload}
          className="glass-lime text-lime-400 px-4 py-2 rounded-lg font-medium hover:text-lime-300 transition-colors border border-lime-500/50 hover:border-lime-400/70 shadow-glow-lime-sm inline-flex items-center"
        >
          <SafeIcon icon={FiDownload} className="mr-2" />
          Download .vcf
        </button>

        <button
          onClick={handleCopy}
          className="glass text-white px-4 py-2 rounded-lg font-medium hover:text-lime-400 transition-colors border border-dark-700 hover:border-lime-500/50 inline-flex items-center"
        >
          <SafeIcon icon={copied ? FiCheck : FiCopy} className={`mr-2 ${copied ? 'text-lime-400' : ''}`} />
          {copied ? 'Copied!' : 'Copy vCard'}
        </button>

        <button
          onClick={() => setShowQR(!showQR)}
          className="glass text-white px-4 py-2 rounded-lg font-medium hover:text-lime-400 transition-colors border border-dark-700 hover:border-lime-500/50 inline-flex items-center"
        >
          <SafeIcon icon={FiDownload} className="mr-2" />
          {showQR ? 'Hide' : 'Show'} QR Code
        </button>
      </div>

      {/* QR Code */}
      {showQR && vcardContent && (
        <div className="mt-6 p-4 bg-dark-800 rounded-lg border border-dark-700">
          <p className="text-sm text-dark-400 mb-3 text-center">
            Scan this QR code to add contact to phone
          </p>
          <div className="flex justify-center">
            <div className="p-4 bg-white rounded-lg">
              <QRCodeSVG
                value={vcardContent}
                size={200}
                level="M"
                includeMargin={true}
              />
            </div>
          </div>
          <p className="text-xs text-dark-500 mt-3 text-center">
            Compatible with iPhone, Android, and most contact apps
          </p>
        </div>
      )}

      {/* Info */}
      <div className="mt-4 p-3 bg-lime-500/10 border border-lime-500/30 rounded-lg">
        <p className="text-xs text-lime-400">
          <strong>Tip:</strong> vCard files can be imported into most contact apps including iPhone Contacts, 
          Google Contacts, Outlook, and more. The QR code can be scanned directly to add the contact.
        </p>
      </div>
    </div>
  );
};

export default VCardExport;

